import { FC, ReactNode, useState } from 'react';
import useAsync from 'react-use/lib/useAsync';
import {
  Column,
  GenericEntityType,
  GenericProviderType,
  KindEnum,
  Pagination,
  Provider,
  TemplateEntity,
  TextFieldValue,
  WbSafeNunjucksApi,
  catalogEntityAdapter,
  contextProviderFactory,
  getLabelAndValue,
  parseDisplayField,
  selectTemplateEntity,
  useColumns,
} from '@agilelab/plugin-wb-platform';
import { SubjectEntry } from '@agilelab/plugin-wb-rbac-common';
import { identityApiRef, useApi } from '@mainstage/core-plugin-api';
import { CatalogApi, catalogApiRef } from '@mainstage/plugin-catalog-react';
import {
  RbacSubjectFilters,
  RbacSubjectFiltersSection,
} from './RbacSubjectFilters';
import { rbacApiRef } from '../../plugin';

export type RbacSubjectEntity = GenericEntityType & { ref: string };

interface RbacSubjectContextType
  extends GenericProviderType<RbacSubjectEntity[], RbacSubjectFilters> {}

const [useRbacSubjectContext, RbacSubjectContextProvider] =
  contextProviderFactory<RbacSubjectContextType>();

export default useRbacSubjectContext;

export interface RbacSubjectProviderProps {
  templateKind: TemplateEntity | undefined;
  children: ReactNode;
  roleIds?: string[];
  scopes?: string[];
  resolveGroups?: boolean;
  pageSize?: number;
}

const DEFAULT_PAGE_SIZE = 20;

const defaultColumns = new Map<string, string>([
  ['name', '{{spec.profile.displayName}}'],
]);

function toRbacSubjectEntity(
  entry: SubjectEntry,
  displayName?: string,
): RbacSubjectEntity {
  const isGroup = entry.subject.startsWith('group:');
  const kind = isGroup ? KindEnum.group : KindEnum.user;
  const name = entry.subject.replace(/^(user:|group:)/, '');

  return {
    kind: isGroup ? 'Group' : 'User',
    apiVersion: 'backstage.io/v1alpha1',
    metadata: { name, namespace: 'default' },
    spec: { profile: { displayName: displayName ?? name } },
    ref: entry.subject,
    __metadata: { kind, name },
  };
}

export const RbacSubjectProvider: FC<RbacSubjectProviderProps> = props => {
  const {
    templateKind,
    children,
    roleIds,
    scopes,
    resolveGroups,
    pageSize = DEFAULT_PAGE_SIZE,
  } = props;

  const rbacApi = useApi(rbacApiRef);
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);
  const [filters, setFilters] = useState<RbacSubjectFilters>({});
  const [count, setCount] = useState<number>(0);
  const [pagination, setPagination] = useState<Pagination>({
    limit: pageSize,
    currentPage: 0,
  });
  const [nextCursor, setNextCursor] = useState<string | undefined>(undefined);
  const [prevCursor, setPrevCursor] = useState<string | undefined>(undefined);

  const columns: Column[] = useColumns(defaultColumns, templateKind);

  const subjectsState = useAsync(async () => {
    const offset = pagination.cursor ? parseInt(pagination.cursor, 10) : 0;
    const token = (await identityApi.getCredentials()).token;

    const result = await rbacApi.getSubjects({
      roleIds,
      scopes,
      resolveGroups,
      search: filters.search || undefined,
      limit: pagination.limit,
      offset,
      options: { token },
    });

    setCount(result.total);

    const hasNext = offset + result.subjects.length < result.total;
    const hasPrev = offset > 0;
    setNextCursor(hasNext ? String(offset + pagination.limit) : undefined);
    setPrevCursor(
      hasPrev ? String(Math.max(0, offset - pagination.limit)) : undefined,
    );

    // Enrich subjects with display names from the catalog
    const entityRefs = result.subjects.map(s => s.subject);
    const displayNames: Map<string, string> = new Map();
    try {
      const catalogResult = await catalogApi.getEntitiesByRefs({
        entityRefs,
        fields: [
          'kind',
          'metadata.name',
          'metadata.namespace',
          'spec.profile.displayName',
        ],
      });
      catalogResult.items.forEach(entity => {
        if (!entity) return;
        const ref = `${entity.kind.toLowerCase()}:${
          entity.metadata.namespace ?? 'default'
        }/${entity.metadata.name}`;
        const dn = (entity as any).spec?.profile?.displayName;
        if (dn) displayNames.set(ref, dn);
      });
    } catch {
      // Catalog lookup is best-effort; fall back to raw names
    }

    return result.subjects.map(s =>
      toRbacSubjectEntity(s, displayNames.get(s.subject)),
    );
  }, [
    templateKind,
    filters,
    pagination.limit,
    pagination.cursor,
    roleIds,
    scopes,
    resolveGroups,
  ]);

  return (
    <RbacSubjectContextProvider
      value={{
        entitiesState: subjectsState,
        columns,
        filters,
        changeFilters: <K extends keyof RbacSubjectFilters>(
          key: K,
          filterValue: RbacSubjectFilters[K],
        ) => {
          setPagination(p => ({ ...p, currentPage: 0, cursor: undefined }));
          setFilters(f => ({ ...f, [key]: filterValue }));
        },
        resetFilters: () => {
          setFilters({});
        },
        templateKind,
        pagination,
        setPagination,
        count,
        nextCursor,
        prevCursor,
      }}
    >
      {children}
    </RbacSubjectContextProvider>
  );
};

export interface RbacSubjectHandlerOptions {
  roleIds?: string[];
  scopes?: string[];
  resolveGroups?: boolean;
  pageSize?: number;
}

export class RbacSubjectHandlerProvider
  implements Provider<RbacSubjectEntity[], RbacSubjectFilters>
{
  private readonly nunjucksApi: WbSafeNunjucksApi;
  private readonly catalogApi: CatalogApi;
  private readonly templateEntity: TemplateEntity | undefined;
  private readonly roleIds?: string[];
  private readonly scopes?: string[];
  private readonly resolveGroups?: boolean;
  private readonly pageSize?: number;

  constructor(
    nunjucksApi: WbSafeNunjucksApi,
    catalogApi: CatalogApi,
    availableKinds: TemplateEntity[],
    options: RbacSubjectHandlerOptions,
  ) {
    this.nunjucksApi = nunjucksApi;
    this.catalogApi = catalogApi;
    this.roleIds = options.roleIds;
    this.scopes = options.scopes;
    this.resolveGroups = options.resolveGroups;
    this.pageSize = options.pageSize;

    this.templateEntity = selectTemplateEntity(availableKinds, KindEnum.user);
  }

  useContext = useRbacSubjectContext;

  renderContextProvider(
    templateKind: TemplateEntity | undefined,
    children: ReactNode,
  ) {
    return (
      <RbacSubjectProvider
        templateKind={templateKind}
        roleIds={this.roleIds}
        scopes={this.scopes}
        resolveGroups={this.resolveGroups}
        pageSize={this.pageSize}
      >
        {children}
      </RbacSubjectProvider>
    );
  }

  filtersSection = RbacSubjectFiltersSection;

  adapt(entity: GenericEntityType) {
    const parsedDisplayField = parseDisplayField(
      this.nunjucksApi,
      entity,
      this.templateEntity?.displayField,
    );
    return catalogEntityAdapter(entity, parsedDisplayField, true);
  }

  async resolve(ref: string): Promise<TextFieldValue | undefined> {
    if (!ref.startsWith('user:') && !ref.startsWith('group:')) {
      return undefined;
    }

    const name = ref.replace(/^(user:|group:)/, '');
    const isGroup = ref.startsWith('group:');
    const kind = isGroup ? KindEnum.group : KindEnum.user;

    // Try to fetch display name from catalog
    let displayName: string | undefined;
    try {
      const catalogEntity = await this.catalogApi.getEntityByRef(ref);
      if (catalogEntity) {
        displayName = (catalogEntity as any).spec?.profile?.displayName;
      }
    } catch {
      // Best-effort; fall back to raw name
    }

    const entity: RbacSubjectEntity = {
      kind: isGroup ? 'Group' : 'User',
      apiVersion: 'backstage.io/v1alpha1',
      metadata: { name, namespace: 'default' },
      spec: { profile: { displayName: displayName ?? name } },
      ref,
      __metadata: { kind, name },
    };

    return getLabelAndValue(entity, this.adapt(entity), this.templateEntity);
  }

  extractEntityId = (entity: GenericEntityType): string | undefined => {
    return (entity as RbacSubjectEntity).ref;
  };
}
